import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { baseAPIRoute } from '../routes/index';
import Favourites from '../../../web/components/Favourites';
import Plot from '../components/Plot';
import BedroomCheckBox from '../components/BedroomCheckBox';

const defaultBedrooms = ['1', '2', '3', '4', '5'];
const defaultStatuses = ['available', 'sold', 'coming-soon'];
const statusesExcludedFromPriceSort = ['sold', 'coming-soon'];

const AvailablePlots = () => {
  const [plots, setPlots] = useState([]);
  const [filteredPlots, setFilteredPlots] = useState([]);
  const [loadedPlots, setLoadedPlots] = useState([]);
  const plotsPerLoad = 12;
  const [loadedPlotsCount, setLoadedPlotsCount] = useState(12);

  const [readyToMoveInto, setReadyToMoveInto] = useState(false);

  // Filter State
  const [bedrooms, setBedrooms] = useState([]);
  const [status, setStatus] = useState('default');
  const [sortBy, setSortBy] = useState('default');

  // UI
  const [bedroomFilter, setBedroomFilter] = useState(false);
  const [loading, setLoading] = useState(true);

  const ref = useRef();

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setBedroomFilter(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  useEffect(() => {
    Favourites();
  }, [filteredPlots]);

  const getSortPrice = (plot) => +(plot.offers_over_price || plot.price || 0);

  const filterPlots = useCallback(() => {
    const bedroomsArr = bedrooms.length >= 1 ? bedrooms : defaultBedrooms;
    const statusArr =
      Array.isArray(status) && status.length >= 1 ? status : defaultStatuses;

    const filteredPlotsArr = plots.filter(
      (plot) =>
        bedroomsArr.some((value) => value === plot.bedrooms) &&
        statusArr.some((value) => value === plot.status) &&
        (sortBy === 'default' ||
          !statusesExcludedFromPriceSort.includes(plot.status)) &&
        (!readyToMoveInto || plot.ready_to_move_into === true),
    );

    if (sortBy !== 'default') {
      const newArray = filteredPlotsArr.sort((a, b) =>
        sortBy === 'asc'
          ? getSortPrice(a) - getSortPrice(b)
          : getSortPrice(b) - getSortPrice(a),
      );
      setFilteredPlots(newArray);
      setLoadedPlots(newArray.slice(0, loadedPlotsCount));
    } else {
      setFilteredPlots(filteredPlotsArr);
      setLoadedPlots(filteredPlotsArr.slice(0, loadedPlotsCount));
    }
  }, [
    setFilteredPlots,
    bedrooms,
    plots,
    status,
    sortBy,
    loadedPlotsCount,
    readyToMoveInto,
  ]);

  const fetchPlots = useCallback(async () => {
    const getDevelopmentId =
      document.querySelector('#react-app').attributes[1].value;
    const fetch = await axios(`${baseAPIRoute}/plots?id=${getDevelopmentId}`);
    if (fetch) {
      setPlots([...fetch.data.items]);
    }
  }, []);

  useEffect(() => {
    fetchPlots();
  }, [fetchPlots]);

  useEffect(() => {
    if (plots.length >= 1) {
      filterPlots();
    }
  }, [
    plots,
    filterPlots,
    sortBy,
    status,
    bedrooms,
    loadedPlotsCount,
    readyToMoveInto,
  ]);

  useEffect(() => {
    if (filteredPlots.length >= 1) {
      setLoading(false);
    }
  }, [setLoading, filteredPlots]);

  const isBedroomActive = (value) =>
    bedrooms.some((bedroom) => bedroom === value);

  const toggleBedFilter = (e) => {
    const { value: bedroomNumber } = e.target;
    const checkIfBedroomIsAlreadyInState = isBedroomActive(bedroomNumber);
    if (checkIfBedroomIsAlreadyInState) {
      const newBedroomState = bedrooms.filter(
        (bedroom) => bedroom !== bedroomNumber,
      );
      setBedrooms(newBedroomState);
    } else {
      setBedrooms([...bedrooms, bedroomNumber]);
    }
  };

  const onStatusChange = (e) => {
    const newStatus = e.target.value;

    setStatus([newStatus]);

    if (statusesExcludedFromPriceSort.includes(newStatus)) {
      setSortBy('default');
    }
  };

  const onSortByChange = (e) => {
    const newSortBy = e.target.value;
    const currentStatus = Array.isArray(status) ? status[0] : status;

    setSortBy(newSortBy);

    if (
      newSortBy !== 'default' &&
      statusesExcludedFromPriceSort.includes(currentStatus)
    ) {
      setStatus('default');
    }
  };

  const resetFilters = () => {
    setBedrooms([]);
    setStatus('default');
    setSortBy('default');
    setReadyToMoveInto(false);
  };

  // Returns a boolean true or false
  const userHasFiltered =
    bedrooms.length >= 1 ||
    (Array.isArray(status) && status.length >= 1) ||
    sortBy !== 'default';

  const onLoadMore = () => {
    setLoadedPlotsCount(loadedPlotsCount + plotsPerLoad);
  };

  return (
    plots.length >= 1 && (
      <section className="section plots__section" id="availablehomes">
        <div className="container">
          <div className="plots__header">
            <h3>Availability</h3>
            <div
              className={`plots__filters ${
                userHasFiltered && 'plots__filters--filtered'
              }`}
            >
              {userHasFiltered && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="plots__filter-reset-button"
                >
                  reset
                </button>
              )}
              <div className="plots__filter">
                <button
                  type="button"
                  className="plots__filter-button"
                  onClick={() => setBedroomFilter(!bedroomFilter)}
                >
                  {bedrooms.length >= 1
                    ? `${bedrooms.sort((a, b) => a - b).join(', ')} Beds`
                    : 'Bedrooms'}
                </button>
                {bedroomFilter && (
                  <div className="plots__filter-drop" ref={ref}>
                    {defaultBedrooms.map((bedroom, index) => (
                      <BedroomCheckBox
                        key={index}
                        type="button"
                        value={bedroom}
                        onClick={(e) => toggleBedFilter(e)}
                        isBedroomActive={isBedroomActive(bedroom)}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="plots__filter">
                <select onChange={onStatusChange} value={status}>
                  <option value="default" disabled>
                    Status
                  </option>
                  <option value="available">Available</option>
                  <option value="coming-soon">Coming Soon</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
              <div className="plots__filter">
                <select onChange={onSortByChange} value={sortBy}>
                  <option value="default" disabled>
                    Sort By Price
                  </option>
                  <option value="asc">Low to High Price</option>
                  <option value="desc">High to Low Price</option>
                </select>
              </div>
              <div className="plots__filter plots__filter--checkbox">
                <label
                  className="plots__filter-checkbox-label"
                  htmlFor="ready-to-move-into"
                >
                  <input
                    type="checkbox"
                    id="ready-to-move-into"
                    checked={readyToMoveInto}
                    onChange={(e) => setReadyToMoveInto(e.target.checked)}
                  />
                  <span className="plots__filter-custom-checkbox" />
                  Ready to Move Into
                </label>
              </div>
            </div>
          </div>
          {loading && (
            <section className="plots__message">
              <p className="plots__message-text plots__message-text--grey">
                Loading...
              </p>
            </section>
          )}
          {!loading && loadedPlots.length >= 1 ? (
            <div className="plots">
              {loadedPlots.map(
                (plot, index) =>
                  !plot.hide_on_availability_section && (
                    <Plot
                      plot={plot}
                      key={index}
                      favourites={() => Favourites()}
                    />
                  ),
              )}
            </div>
          ) : (
            !loading && (
              <section className="plots__message">
                <p className="plots__message-text">
                  Sorry, we don’t have any homes available within your search
                  criteria
                </p>
                <p className="plots__message-text plots__message-text--small">
                  Try altering your filters or enquire about future availability
                </p>
              </section>
            )
          )}
          {!loading && loadedPlotsCount < filteredPlots.length && (
            <div className="plots__button">
              <button
                type="button"
                onClick={onLoadMore}
                className="button button--clear"
              >
                show More
              </button>
            </div>
          )}
        </div>
      </section>
    )
  );
};

export default AvailablePlots;